import { ThemeOptions, createTheme } from '@mui/material/styles';

import React from 'react';

export const colors = {
    raspberryPink: "#EA728C",
    white: "#FFFFFF",
    black: "#000000",
    softGreen: "#6FC783",
    dimGray: "#4D4D4D",
    iceGray: "#F8F9Fb",
    semiTransparentWhite: "#FFFFFFB3",
    mediumGray: "#888"
};

// Extend the theme to add the 'hint' property to the `text` palette
declare module '@mui/material/styles' {
    interface TypeText {
        hint?: string;
        black?: string;
    }

    interface TypeOther {
        darkBlue?: string;
        lightBlack?: string;
    }

    interface PaletteOptions {
        text?: Partial<TypeText>;
        other?: Partial<TypeOther>;
    }
}

// Extend the theme to add the 'hint' property to the `background` palette
declare module '@mui/material/styles' {
    interface TypeBackground {
        bgSoftGreen?: string;
        dimGray?: string;
        iceGray?: string;
        transparentWhite?: string;
        mediumGray?: string;
    }

    interface PaletteOptions {
        background?: Partial<TypeBackground>;
        body?: Partial<TypeBackground>;
    }
}

declare module '@mui/material/styles' {
    interface TypographyVariants {
        h1_Thin: React.CSSProperties;
        h3_Thin: React.CSSProperties;
        h4_Thin: React.CSSProperties;
        h6_Thin: React.CSSProperties;
        h6_Bold: React.CSSProperties;
        h7: React.CSSProperties;
        h8: React.CSSProperties;
        h9: React.CSSProperties;
        subtitle3: React.CSSProperties;
        subtitle3_Thin: React.CSSProperties;
        subtitle4: React.CSSProperties;
        subtitle4_Thin: React.CSSProperties;
    }

    // allow configuration using `createTheme()`
    interface TypographyVariantsOptions {
        h1_Thin?: React.CSSProperties;
        h2_Thin?: React.CSSProperties;
        h3_Thin?: React.CSSProperties;
        h4_Thin?: React.CSSProperties;
        h6_Thin?: React.CSSProperties;
        h6_Bold?: React.CSSProperties;
        h7: React.CSSProperties;
        h8: React.CSSProperties;
        h9: React.CSSProperties;
        subtitle3: React.CSSProperties;
        subtitle3_Thin: React.CSSProperties;
        subtitle4: React.CSSProperties;
        subtitle4_Thin: React.CSSProperties;
    }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
    interface TypographyPropsVariantOverrides {
        h1_Thin: true;
        h4_Thin: true;
        h6_Thin: true;
        h6_Bold: true;
        h2_Thin: true;
        h3_Thin: true;
        h7: true;
        h8: true;
        h9: true;
        subtitle3: true;
        subtitle3_Thin: true;
        subtitle4: true;
        subtitle4_Thin: true;
    }
}

const themeOptions: ThemeOptions = {
    breakpoints: {
        values: {
            xs: 700,
            sm: 900, // Small screens start from 900px
            md: 1300, // Medium screens start from 1300px
            lg: 1920, // Large screens start from 1920px (optional)
            xl: 2560, // Extra large screens
        },
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: '8px',
                    textTransform: 'none',
                },
            },
        },
    },
    palette: {  
        primary: {
            dark: colors.black,
            light: colors.white,
            main: colors.raspberryPink
        },
        body: {
            bgSoftGreen: colors.softGreen,
            dimGray: colors.dimGray,
            transparentWhite: colors.semiTransparentWhite,
            mediumGray: colors.mediumGray
        },
        background: {
            iceGray: colors.iceGray
        }
      },
};

const theme = createTheme(themeOptions);
theme.typography = {
    ...theme.typography,
    fontFamily: 'Rubik, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji',
    h1: {
        fontFamily: 'Rubik, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji',
        fontSize: '4rem',
        fontStyle: 'normal',
        fontWeight: 700,
        lineHeight: 'normal',
        [theme.breakpoints.down('xl')]: { fontSize: '5rem' },
        [theme.breakpoints.down('lg')]: { fontSize: '4rem' },
        [theme.breakpoints.down('md')]: { fontSize: '3.75rem' },
        [theme.breakpoints.down('sm')]: { fontSize: '3rem' },
        [theme.breakpoints.down('xs')]: { fontSize: '2.5rem' },
    },
    h1_Thin: {
        fontFamily: 'Rubik, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji',
        fontSize: '4rem',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: 'normal',
        [theme.breakpoints.down('xl')]: { fontSize: '5rem' },
        [theme.breakpoints.down('lg')]: { fontSize: '4rem' },
        [theme.breakpoints.down('md')]: { fontSize: '3.5rem' },
        [theme.breakpoints.down('sm')]: { fontSize: '2.75rem' },
        [theme.breakpoints.down('xs')]: { fontSize: '2.25rem' },
    },
    h2: {
        fontFamily: 'Rubik, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji',
        fontSize: '3.5rem',
        fontStyle: 'normal',
        fontWeight: 700,
        lineHeight: '5.375rem',
        [theme.breakpoints.down('xl')]: { fontSize: '4rem' },
        [theme.breakpoints.down('lg')]: { fontSize: '3.5rem' },
        [theme.breakpoints.down('md')]: { fontSize: '3rem' },
        [theme.breakpoints.down('sm')]: { fontSize: '2.5rem' },
        [theme.breakpoints.down('xs')]: { fontSize: '2rem' },
    },
    h3: {
        fontFamily: 'Rubik, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji',
        fontSize: '1.625rem',
        fontStyle: 'normal',
        fontWeight: 700,
        lineHeight: 'normal',
        [theme.breakpoints.down('xl')]: { fontSize: '1.75rem' },
        [theme.breakpoints.down('lg')]: { fontSize: '1.625rem' },
        [theme.breakpoints.down('md')]: { fontSize: '1.5rem' },
        [theme.breakpoints.down('sm')]: { fontSize: '1.375rem' },
        [theme.breakpoints.down('xs')]: { fontSize: '1.25rem' },
    },
    h3_Thin: {
        fontFamily: 'Rubik, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji',
        fontSize: '1.625rem',
        fontStyle: 'normal',
        fontWeight: 500,
        lineHeight: 'normal',
        [theme.breakpoints.down('xl')]: { fontSize: '1.75rem' },
        [theme.breakpoints.down('lg')]: { fontSize: '1.625rem' },
        [theme.breakpoints.down('md')]: { fontSize: '1.5rem' },
        [theme.breakpoints.down('sm')]: { fontSize: '1.375rem' },
        [theme.breakpoints.down('xs')]: { fontSize: '1.25rem' },
    },
    h4: {
        fontFamily: 'Rubik, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji',
        fontSize: '1.25rem',
        fontStyle: 'normal',
        fontWeight: 700,
        lineHeight: 'normal',
        [theme.breakpoints.down('xl')]: { fontSize: '1.375rem' },
        [theme.breakpoints.down('lg')]: { fontSize: '1.25rem' },
        [theme.breakpoints.down('md')]: { fontSize: '1.1875rem' },
        [theme.breakpoints.down('sm')]: { fontSize: '1.125rem' },
        [theme.breakpoints.down('xs')]: { fontSize: '1rem' },
    },
    h4_Thin: {
        fontFamily: 'Rubik, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji',
        fontSize: '1.25rem',
        fontStyle: 'normal',
        fontWeight: 500,
        lineHeight: 'normal',
        [theme.breakpoints.down('xl')]: { fontSize: '1.375rem' },
        [theme.breakpoints.down('lg')]: { fontSize: '1.25rem' },
        [theme.breakpoints.down('md')]: { fontSize: '1.1875rem' },
        [theme.breakpoints.down('sm')]: { fontSize: '1.125rem' },
        [theme.breakpoints.down('xs')]: { fontSize: '1rem' },
    },
    h5: {
        fontFamily: 'Rubik, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji',
        fontSize: '1.125rem',
        fontStyle: 'normal',
        fontWeight: 700,
        lineHeight: 'normal',
        [theme.breakpoints.down('xl')]: { fontSize: '1.25rem' },
        [theme.breakpoints.down('lg')]: { fontSize: '1.125rem' },
        [theme.breakpoints.down('md')]: { fontSize: '1.0625rem' },
        [theme.breakpoints.down('sm')]: { fontSize: '1rem' },
        [theme.breakpoints.down('xs')]: { fontSize: '0.875rem' },
    },
    h6: {
        fontFamily: 'Rubik, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji',
        fontSize: '1rem',
        fontStyle: 'normal',
        fontWeight: 700,
        lineHeight: 'normal',
        [theme.breakpoints.down('xl')]: { fontSize: '1.125rem' },
        [theme.breakpoints.down('lg')]: { fontSize: '1rem' },
        [theme.breakpoints.down('md')]: { fontSize: '0.9375rem' },
        [theme.breakpoints.down('sm')]: { fontSize: '0.875rem' },
        [theme.breakpoints.down('xs')]: { fontSize: '0.8125rem' },
    },
    h6_Bold: {
        fontFamily: 'Rubik, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji',
        fontSize: '1rem',
        fontStyle: 'normal',
        fontWeight: 500,
        lineHeight: 'normal',
        [theme.breakpoints.down('xl')]: { fontSize: '1.125rem' },
        [theme.breakpoints.down('lg')]: { fontSize: '1rem' },
        [theme.breakpoints.down('md')]: { fontSize: '0.9375rem' },
        [theme.breakpoints.down('sm')]: { fontSize: '0.875rem' },
        [theme.breakpoints.down('xs')]: { fontSize: '0.8125rem' },
    },
    h6_Thin: {
        fontFamily: 'Rubik, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji',
        fontSize: '1rem',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: 'normal',
        [theme.breakpoints.down('xl')]: { fontSize: '1.125rem' },
        [theme.breakpoints.down('lg')]: { fontSize: '1rem' },
        [theme.breakpoints.down('md')]: { fontSize: '0.9375rem' },
        [theme.breakpoints.down('sm')]: { fontSize: '0.875rem' },
        [theme.breakpoints.down('xs')]: { fontSize: '0.8125rem' },
    },
    h7: {
        fontFamily: 'Rubik, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji',
        fontSize: '0.9375rem',
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: '1.25rem',
        [theme.breakpoints.down('xl')]: { fontSize: '1rem' },
        [theme.breakpoints.down('lg')]: { fontSize: '0.9375rem' },
        [theme.breakpoints.down('md')]: { fontSize: '0.875rem' },
        [theme.breakpoints.down('sm')]: { fontSize: '0.8125rem' },
        [theme.breakpoints.down('xs')]: { fontSize: '0.75rem' },
    },
    h8: {
        fontFamily: 'Rubik, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji',
        fontSize: '0.875rem',
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: 'normal',
        [theme.breakpoints.down('xl')]: { fontSize: '0.9375rem' },
        [theme.breakpoints.down('lg')]: { fontSize: '0.875rem' },
        [theme.breakpoints.down('md')]: { fontSize: '0.8125rem' },
        [theme.breakpoints.down('sm')]: { fontSize: '0.75rem' },
        [theme.breakpoints.down('xs')]: { fontSize: '0.6875rem' },
    },
    h9: {
        fontFamily: 'Rubik, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji',
        fontSize: '0.75rem',
        fontStyle: 'normal',
        fontWeight: 700,
        lineHeight: 'normal',
        [theme.breakpoints.down('xl')]: { fontSize: '0.9375rem' },
        [theme.breakpoints.down('lg')]: { fontSize: '0.875rem' },
        [theme.breakpoints.down('md')]: { fontSize: '0.8125rem' },
        [theme.breakpoints.down('sm')]: { fontSize: '0.75rem' },
        [theme.breakpoints.down('xs')]: { fontSize: '0.6875rem' },
    },
    subtitle1: {
        fontFamily: 'Rubik, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji',
        fontSize: '2rem',
        fontStyle: 'normal',
        fontWeight: 600,
        lineHeight: 'normal',
        [theme.breakpoints.down('xl')]: { fontSize: '2.25rem' },
        [theme.breakpoints.down('lg')]: { fontSize: '2rem' },
        [theme.breakpoints.down('md')]: { fontSize: '1.875rem' },
        [theme.breakpoints.down('sm')]: { fontSize: '1.75rem' },
        [theme.breakpoints.down('xs')]: { fontSize: '1.5rem' },
    },
    subtitle2: {
        fontFamily: 'Rubik, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji',
        fontSize: '1.375rem',
        fontStyle: 'normal',
        fontWeight: 700,
        lineHeight: 'normal',
        [theme.breakpoints.down('xl')]: { fontSize: '1.5rem' },
        [theme.breakpoints.down('lg')]: { fontSize: '1.375rem' },
        [theme.breakpoints.down('md')]: { fontSize: '1.25rem' },
        [theme.breakpoints.down('sm')]: { fontSize: '1.125rem' },
        [theme.breakpoints.down('xs')]: { fontSize: '1rem' },
    },
    subtitle3: {
        fontFamily: 'Rubik, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji',
        fontSize: '1.5rem',
        fontStyle: 'normal',
        fontWeight: 700,
        lineHeight: 'normal',
        [theme.breakpoints.down('xl')]: { fontSize: '1.8rem' },
        [theme.breakpoints.down('lg')]: { fontSize: '1.5rem' },
        [theme.breakpoints.down('md')]: { fontSize: '1.25rem' },
        [theme.breakpoints.down('sm')]: { fontSize: '1.125rem' },
        [theme.breakpoints.down('xs')]: { fontSize: '1rem' },
    },
    subtitle3_Thin: {
        fontFamily: 'Rubik, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji',
        fontSize: '1.5rem',
        fontStyle: 'normal',
        fontWeight: 500,
        lineHeight: 'normal',
        [theme.breakpoints.down('xl')]: { fontSize: '1.8rem' },
        [theme.breakpoints.down('lg')]: { fontSize: '1.5rem' },
        [theme.breakpoints.down('md')]: { fontSize: '1.25rem' },
        [theme.breakpoints.down('sm')]: { fontSize: '1.125rem' },
        [theme.breakpoints.down('xs')]: { fontSize: '1rem' },
    },
    subtitle4: {
        fontFamily: 'Rubik, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji',
        fontSize: '1.75rem',
        fontStyle: 'normal',
        fontWeight: 700,
        lineHeight: 'normal',
        [theme.breakpoints.down('xl')]: { fontSize: '1.8rem' },
        [theme.breakpoints.down('lg')]: { fontSize: '1.5rem' },
        [theme.breakpoints.down('md')]: { fontSize: '1.25rem' },
        [theme.breakpoints.down('sm')]: { fontSize: '1.125rem' },
        [theme.breakpoints.down('xs')]: { fontSize: '1rem' },
    },
    subtitle4_Thin: {
        fontFamily: 'Rubik, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji',
        fontSize: '1.75rem',
        fontStyle: 'normal',
        fontWeight: 500,
        lineHeight: 'normal',
        [theme.breakpoints.down('xl')]: { fontSize: '1.8rem' },
        [theme.breakpoints.down('lg')]: { fontSize: '1.5rem' },
        [theme.breakpoints.down('md')]: { fontSize: '1.25rem' },
        [theme.breakpoints.down('sm')]: { fontSize: '1.125rem' },
        [theme.breakpoints.down('xs')]: { fontSize: '1rem' },
    },
};
export default theme;
